package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.Machine
import com.kaihuoy.shared.data.Tyomaa
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import kotlin.js.Date

@Composable
fun MachineScreen(user: User?, isAdmin: Boolean, standalone: Boolean = false, onLogout: () -> Unit) {
    val canManage = isAdmin || (user?.isHuolto == true)
    var showAddForm by remember { mutableStateOf(false) }
    var editingMachine by remember { mutableStateOf<Machine?>(null) }
    var machines by remember { mutableStateOf<List<Machine>>(emptyList()) }
    var tyomaat by remember { mutableStateOf<List<Tyomaa>>(emptyList()) }
    var filterTyomaa by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    fun fetchMachines() {
        if (user == null) return
        scope.launch {
            isLoading = true
            try {
                FirebaseService.database.reference("machines").valueEvents.collect { snapshot ->
                    machines = snapshot.children.mapNotNull { child ->
                        try {
                            child.value<Machine>()
                        } catch (e: Exception) {
                            val rawValue = try {
                                child.value<Any?>()
                            } catch (ex: Exception) {
                                "unavailable"
                            }
                            console.error("Yksittäisen koneen lataus epäonnistui: ${child.key}, data: ", rawValue, e)
                            null
                        }
                    }.sortedBy { it.machineNumber }
                    isLoading = false
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Normaali peruutus
            } catch (e: Exception) {
                console.error("Koneiden lataus epäonnistui", e)
                if (e.message?.contains("PERMISSION_DENIED") == true) {
                    com.kaihuoy.components.common.AppFeedback.showError("Pääsy kielletty koneiden hallintaan. Varmista, että sinulla on ylläpitäjän oikeudet.")
                }
                isLoading = false
            }
        }
    }

    LaunchedEffect(Unit) {
        fetchMachines()
        scope.launch {
            try {
                FirebaseService.database.reference("tyomaat").valueEvents.collect { snapshot ->
                    tyomaat = snapshot.children.mapNotNull { child ->
                        try { child.value<Tyomaa>() } catch (e: Exception) { null }
                    }.sortedBy { it.nimi }
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
            } catch (e: Exception) {
                console.error("Työmaiden lataus epäonnistui", e)
            }
        }
    }

    when {
        showAddForm -> AddMachineForm(user, onBack = { showAddForm = false })
        editingMachine != null -> EditMachineForm(editingMachine!!, onBack = { editingMachine = null })
        else -> PageLayout(greeting = getGreeting(user?.displayName), onLogout = onLogout) {
            Div(attrs = {
                style {
                    padding(16.px)
                    boxSizing("border-box")
                    width(100.percent)
                }
            }) {
                if (standalone) {
                    H1(attrs = { style { margin(0.px, 0.px, 8.px, 0.px); color(KaihuTheme.TextPrimary) } }) {
                        Text("Koneet")
                    }
                    P(attrs = { style { margin(0.px, 0.px, 24.px, 0.px); color(KaihuTheme.TextSecondary) } }) {
                        Text("Kaluston hallinta ja tilatiedot")
                    }
                }

                // Suodatus
                val filteredMachines = if (filterTyomaa.isEmpty()) machines
                    else machines.filter { it.worksite == filterTyomaa }

                // Tilasummary
                val inUse = machines.count { it.status == "käytössä" }
                val onSite = machines.count { it.status == "työmaalla" }
                val free = machines.count { it.status != "käytössä" && it.status != "työmaalla" }
                val statuses = listOf("käytössä", "ei käytössä", "työmaalla")

                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        marginBottom(24.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    StatPill("Käytössä", inUse, Color("#DCFCE7"), Color("#15803D"), icon = "circle-check")
                    StatPill("Työmaalla", onSite, Color("#FEF9C3"), Color("#A16207"), icon = "circle-check")
                    StatPill("Ei käytössä", free, Color("#FFF1F2"), Color("#BE123C"), icon = "circle-check")
                }

                // Työmaa-filtteri + "Lisää uusi kone" -rivi
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(20.px)
                        flexWrap(FlexWrap.Wrap)
                        gap(8.px)
                    }
                }) {
                    // Työmaa-filtteri
                    Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); property("min-width", "220px"); property("max-width", "320px"); property("flex", "1") } }) {
                        TIcon("filter", 1.em)
                        Div(attrs = { style { property("flex", "1") } }) {
                            StyledDropdown(
                                value = filterTyomaa,
                                options = listOf(DropdownOption("", "Kaikki työmaat")) +
                                    tyomaat.map { DropdownOption(it.nimi, it.nimi) },
                                placeholder = "Kaikki työmaat",
                                onSelect = { filterTyomaa = it }
                            )
                        }
                        if (filterTyomaa.isNotEmpty()) {
                            Span(attrs = {
                                onClick { filterTyomaa = "" }
                                style { cursor("pointer"); color(KaihuTheme.TextSecondary); fontSize(0.85.em); property("white-space", "nowrap") }
                            }) { Text("✕ Tyhjennä") }
                        }
                    }
                    if (canManage) {
                        StyledButton(
                            "Lisää uusi kone",
                            onClick = { showAddForm = true },
                            backgroundColor = KaihuTheme.Green,
                            icon = "plus"
                        )
                    }
                }

                // Tulosten määrä
                P(attrs = { style { margin(0.px, 0.px, 12.px, 0.px); color(KaihuTheme.TextSecondary); fontSize(0.9.em) } }) {
                    Text(if (filterTyomaa.isEmpty()) "${machines.size} konetta yhteensä"
                         else "${filteredMachines.size} / ${machines.size} konetta")
                }

                if (isLoading) {
                    P { Text("Ladataan koneita...") }
                } else if (filteredMachines.isEmpty()) {
                    P(attrs = { style { color(KaihuTheme.TextSecondary) } }) {
                        Text(if (filterTyomaa.isEmpty()) "Ei koneita tietokannassa." else "Ei koneita työmaavalinnalla \"$filterTyomaa\".")
                    }
                } else {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("repeat(auto-fill, minmax(min(280px, 100%), 1fr))")
                            gap(20.px)
                        }
                    }) {
                        filteredMachines.forEach { machine ->
                            MachineItem(
                                machine,
                                canManage = canManage,
                                tyomaat = tyomaat,
                                onDelete = { fetchMachines() },
                                onEdit = { editingMachine = it })
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun StatPill(label: String, count: Int, bg: CSSColorValue, fg: CSSColorValue, icon: String) {
    Div(attrs = {
        style {
            backgroundColor(bg); color(fg); padding(8.px, 16.px)
            borderRadius(999.px); fontSize(0.9.em); fontWeight("600")
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
        }
    }) {
        TIcon(icon, 1.5.em)
        if (label.isNotBlank()) {
            Span(attrs = { style { fontSize(1.em); fontWeight("800"); color(fg); lineHeight("1") } }) {
                Text("$label: $count")
            }
        }
    }
}


@Composable
fun MachineItem(machine: Machine, canManage: Boolean, tyomaat: List<Tyomaa>, onDelete: () -> Unit, onEdit: (Machine) -> Unit) {
    val scope = rememberCoroutineScope()

    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            property("background", if (hovered) "linear-gradient(135deg, #1e2235 0%, #0f1f2e 100%)" else KaihuTheme.CardGradient)
            padding(20.px)
            borderRadius(16.px)
            property("border", "1px solid ${if (hovered) KaihuTheme.PrimaryTeal else KaihuTheme.Border}")
            property("box-shadow", if (hovered) "0 4px 20px rgba(34,211,238,0.12)" else KaihuTheme.ShadowMd)
            property("transition", "all 0.18s")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexStart); gap(8.px) } }) {
            H3(attrs = { style { margin(0.px); color(KaihuTheme.PrimaryTeal); flex(1) } }) {
                Text("#${machine.machineNumber} - ${machine.manufacturer} ${machine.model}")
            }
        }

        // Statusnapit
        Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); flexWrap(FlexWrap.Wrap) } }) {
            data class StatusOption(val value: String, val label: String, val activeBg: CSSColorValue, val activeText: CSSColorValue)
            val options = listOf(
                StatusOption("käytössä", "Käytössä", Color("#DCFCE7"), Color("#15803D")),
                StatusOption("ei käytössä", "Ei käytössä", Color("#FFF1F2"), Color("#BE123C")),
                StatusOption("työmaalla", "Työmaalla", Color("#FEF9C3"), Color("#A16207"))
            )
            val currentStatus = machine.status.ifBlank { "ei käytössä" }
            options.forEach { opt ->
                val isActive = currentStatus == opt.value
                Span(attrs = {
                    if (canManage && !isActive) {
                        onClick {
                            scope.launch {
                                try {
                                    FirebaseService.database.reference("machines").child(machine.id).child("status").setValue(opt.value)
                                } catch (ex: Exception) {
                                    com.kaihuoy.components.common.AppFeedback.showError("Tilan päivitys epäonnistui")
                                }
                            }
                        }
                    }
                    style {
                        padding(5.px, 12.px)
                        borderRadius(999.px)
                        fontSize(0.82.em)
                        fontWeight("600")
                        cursor(if (canManage && !isActive) "pointer" else "default")
                        backgroundColor(if (isActive) opt.activeBg else KaihuTheme.CardBackground)
                        color(if (isActive) opt.activeText else KaihuTheme.TextSecondary)
                        property("border", "2px solid ${if (isActive) "transparent" else KaihuTheme.Border}")
                        opacity(if (!canManage && !isActive) 0.5 else 1.0)
                    }
                }) { Text(opt.label) }
            }
        }

        // Työmaavalitsin — näkyy vain kun status on "työmaalla"
        if (machine.status == "työmaalla") {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    padding(8.px, 12.px)
                    borderRadius(10.px)
                    backgroundColor(Color("#2a2000"))
                    property("border", "1px solid #A16207")
                    boxSizing("border-box")
                    width(100.percent)
                    property("overflow", "hidden")
                }
            }) {
                TIcon("map-pin", 1.1.em)
                Span(attrs = { style { color(Color("#FEF9C3")); fontSize(0.85.em); fontWeight("600"); property("white-space", "nowrap") } }) {
                    Text("Työmaa:")
                }
                if (canManage) {
                    Select(attrs = {
                        onChange { e -> val v = e.target.value; scope.launch {
                            try { FirebaseService.database.reference("machines").child(machine.id).child("worksite").setValue(v) }
                            catch (ex: Exception) { com.kaihuoy.components.common.AppFeedback.showError("Työmaan tallennus epäonnistui") }
                        }}
                        style {
                            property("flex", "1")
                            minWidth(0.px)
                            width(0.px)
                            backgroundColor(Color("#1a2535"))
                            color(Color("#ffffff"))
                            property("border", "1px solid rgba(255,255,255,0.08)")
                            borderRadius(8.px)
                            padding(6.px, 8.px)
                            fontSize(0.9.em)
                            cursor("pointer")
                            property("outline", "none")
                        }
                    }) {
                        Option("", attrs = { if (machine.worksite.isBlank()) attr("selected", "selected") }) { Text("Valitse työmaa...") }
                        tyomaat.forEach { t ->
                            Option(t.nimi, attrs = { if (machine.worksite == t.nimi) attr("selected", "selected") }) { Text(t.nimi) }
                        }
                    }
                } else {
                    Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("600"); fontSize(0.9.em); property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap") } }) {
                        Text(if (machine.worksite.isBlank()) "Ei määritetty" else machine.worksite)
                    }
                }
            }
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(8.px)
                fontSize(0.9.em)
                color(KaihuTheme.TextSecondary)
            }
        }) {
            Div(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Sarjanumero: "); Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("600") } }) { Text(machine.serialNumber) } }
            Div(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Vuosimalli: "); Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("600") } }) { Text(machine.manufacturingYear) } }
            Div(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Tunnit: "); Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("600") } }) { Text("${machine.currentHours} h") } }
        }

        if (canManage) {
            Div(attrs = {
                style {
                    property("border-top", "1px solid ${KaihuTheme.Divider}")
                    paddingTop(12.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(8.px)
                }
            }) {
                StyledButton("Muokkaa", onClick = { onEdit(machine) }, backgroundColor = KaihuTheme.PrimaryTeal)
                DelButton("Poista", onClick = {
                    com.kaihuoy.components.common.AppFeedback.confirm(
                        title = "Poistetaanko kone?",
                        message = "Haluatko varmasti poistaa koneen ${machine.machineNumber}?",
                        confirmText = "Poista",
                        confirmColor = KaihuTheme.Danger
                    ) {
                        scope.launch {
                            try {
                                FirebaseService.database.reference("machines").child(machine.id).removeValue()
                                onDelete()
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Kone ${machine.machineNumber} poistettu.")
                            } catch (e: Exception) {
                                com.kaihuoy.components.common.AppFeedback.showError("Poisto epäonnistui")
                            }
                        }
                    }
                }, backgroundColor = KaihuTheme.Danger)
            }
        }
    }
}

@Composable
fun AddMachineForm(user: User?, onBack: () -> Unit) {
    var machineNumber by remember { mutableStateOf("") }
    var manufacturer by remember { mutableStateOf("") }
    var model by remember { mutableStateOf("") }
    var serialNumber by remember { mutableStateOf("") }
    var manufacturingYear by remember { mutableStateOf("") }
    var currentHours by remember { mutableStateOf("") }
    var lastEngineOilHours by remember { mutableStateOf("") }
    var lastHydraulicOilHours by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()

    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Surface)
            padding(32.px)
            borderRadius(16.px)
            property("box-shadow", KaihuTheme.ShadowMd)
            maxWidth(800.px)
            property("margin", "0 auto")
        }
    }) {
        H2 { Text("Lisää uusi kone") }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(16.px)
                marginBottom(24.px)
            }
        }) {
            StyledInput(label = "Koneen numero", value = machineNumber, onValueChange = { machineNumber = it }, placeholder = "Esim. 89")
            StyledInput(label = "Valmistaja", value = manufacturer, onValueChange = { manufacturer = it }, placeholder = "Esim. Volvo")
            StyledInput(label = "Malli", value = model, onValueChange = { model = it }, placeholder = "Esim. A40F")
            StyledInput(label = "Sarjanumero", value = serialNumber, onValueChange = { serialNumber = it })
            StyledInput(label = "Vuosimalli", value = manufacturingYear, onValueChange = { manufacturingYear = it })
            StyledInput(label = "Käyttötunnit (h)", value = currentHours, onValueChange = { currentHours = it })
        }

        H3(attrs = { style { marginBottom(16.px) } }) { Text("Huoltohistoria (tunnit)") }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(16.px)
                marginBottom(32.px)
            }
        }) {
            StyledInput(label = "Moottoriöljy vaihdettu (h)", value = lastEngineOilHours, onValueChange = { lastEngineOilHours = it })
            StyledInput(label = "Hydrauliikkaöljy vaihdettu (h)", value = lastHydraulicOilHours, onValueChange = { lastHydraulicOilHours = it })
        }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); justifyContent(JustifyContent.FlexEnd) } }) {
            StyledButton("Peruuta", onClick = { onBack() }, backgroundColor = KaihuTheme.Grey)
            StyledButton("Tallenna kone", onClick = {
                if (machineNumber.isBlank() || manufacturer.isBlank()) {
                    com.kaihuoy.components.common.AppFeedback.showWarning("Täytä vähintään koneen numero ja valmistaja.")
                    return@StyledButton
                }

                scope.launch {
                    try {
                        val ref = FirebaseService.database.reference("machines").push()
                        val newMachine = Machine(
                            id = ref.key ?: "",
                            machineNumber = machineNumber,
                            manufacturer = manufacturer,
                            model = model,
                            serialNumber = serialNumber,
                            manufacturingYear = manufacturingYear,
                            currentHours = currentHours.toLongOrNull() ?: 0,
                            lastEngineOilChangeHours = lastEngineOilHours.toLongOrNull() ?: 0,
                            lastHydraulicOilChangeHours = lastHydraulicOilHours.toLongOrNull() ?: 0,
                            timestamp = Date.now().toLong(),
                            userId = user?.uid ?: ""
                        )
                        ref.setValue(newMachine)
                        com.kaihuoy.components.common.AppFeedback.showSuccess("Kone tallennettu onnistuneesti!")
                        onBack()
                    } catch (e: Exception) {
                        com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                    }
                }
            }, backgroundColor = KaihuTheme.Primary)
        }
    }
}

@Composable
fun EditMachineForm(machine: Machine, onBack: () -> Unit) {
    var machineNumber by remember { mutableStateOf(machine.machineNumber) }
    var manufacturer by remember { mutableStateOf(machine.manufacturer) }
    var model by remember { mutableStateOf(machine.model) }
    var serialNumber by remember { mutableStateOf(machine.serialNumber) }
    var manufacturingYear by remember { mutableStateOf(machine.manufacturingYear) }
    var currentHours by remember { mutableStateOf(machine.currentHours.toString()) }
    var lastEngineOil by remember { mutableStateOf(machine.lastEngineOilChangeHours.toString()) }
    var lastHydraulicOil by remember { mutableStateOf(machine.lastHydraulicOilChangeHours.toString()) }
    var lastTransmissionOil by remember { mutableStateOf(machine.lastTransmissionOilChangeHours.toString()) }
    var lastBrakeOil by remember { mutableStateOf(machine.lastBrakeOilChangeHours.toString()) }
    val scope = rememberCoroutineScope()

    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Surface)
            padding(32.px)
            borderRadius(16.px)
            property("box-shadow", KaihuTheme.ShadowMd)
            maxWidth(800.px)
            property("margin", "0 auto")
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(24.px) } }) {
            H2(attrs = { style { margin(0.px) } }) { Text("Muokkaa konetta #${machine.machineNumber}") }
            StyledButton("← Takaisin", onClick = onBack, backgroundColor = KaihuTheme.Grey)
        }

        H3(attrs = { style { marginBottom(16.px) } }) { Text("Perustiedot") }
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(16.px)
                marginBottom(24.px)
            }
        }) {
            StyledInput(label = "Koneen numero", value = machineNumber, onValueChange = { machineNumber = it })
            StyledInput(label = "Valmistaja", value = manufacturer, onValueChange = { manufacturer = it })
            StyledInput(label = "Malli", value = model, onValueChange = { model = it })
            StyledInput(label = "Sarjanumero", value = serialNumber, onValueChange = { serialNumber = it })
            StyledInput(label = "Vuosimalli", value = manufacturingYear, onValueChange = { manufacturingYear = it })
            StyledInput(label = "Käyttötunnit (h)", value = currentHours, onValueChange = { currentHours = it })
        }

        H3(attrs = { style { marginBottom(16.px) } }) { Text("Huoltohistoria (tunnit)") }
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1fr 1fr")
                gap(16.px)
                marginBottom(32.px)
            }
        }) {
            StyledInput(label = "Moottoriöljy vaihdettu (h)", value = lastEngineOil, onValueChange = { lastEngineOil = it })
            StyledInput(label = "Hydrauliikkaöljy vaihdettu (h)", value = lastHydraulicOil, onValueChange = { lastHydraulicOil = it })
            StyledInput(label = "Vaihteistoöljy vaihdettu (h)", value = lastTransmissionOil, onValueChange = { lastTransmissionOil = it })
            StyledInput(label = "Jarruöljy vaihdettu (h)", value = lastBrakeOil, onValueChange = { lastBrakeOil = it })
        }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); justifyContent(JustifyContent.FlexEnd) } }) {
            StyledButton("Peruuta", onClick = onBack, backgroundColor = KaihuTheme.Grey)
            StyledButton("Tallenna muutokset", onClick = {
                if (machineNumber.isBlank() || manufacturer.isBlank()) {
                    com.kaihuoy.components.common.AppFeedback.showWarning("Täytä vähintään koneen numero ja valmistaja.")
                    return@StyledButton
                }
                scope.launch {
                    try {
                        val updated = machine.copy(
                            machineNumber = machineNumber,
                            manufacturer = manufacturer,
                            model = model,
                            serialNumber = serialNumber,
                            manufacturingYear = manufacturingYear,
                            currentHours = currentHours.toLongOrNull() ?: machine.currentHours,
                            lastEngineOilChangeHours = lastEngineOil.toLongOrNull() ?: machine.lastEngineOilChangeHours,
                            lastHydraulicOilChangeHours = lastHydraulicOil.toLongOrNull() ?: machine.lastHydraulicOilChangeHours,
                            lastTransmissionOilChangeHours = lastTransmissionOil.toLongOrNull() ?: machine.lastTransmissionOilChangeHours,
                            lastBrakeOilChangeHours = lastBrakeOil.toLongOrNull() ?: machine.lastBrakeOilChangeHours
                        )
                        FirebaseService.database.reference("machines").child(machine.id).setValue(updated)
                        com.kaihuoy.components.common.AppFeedback.showSuccess("Koneen tiedot päivitetty.")
                        onBack()
                    } catch (e: Exception) {
                        com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                    }
                }
            }, backgroundColor = KaihuTheme.Primary)
        }
    }
}